import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, of, throwError } from 'rxjs';
import { AppConfigService } from 'src/app/services/app-config.service';
import { ApiResponse } from 'src/app/shared/interfaces/api-response';
import { IAppConfig } from 'src/app/shared/interfaces/app-config';
import { assertTrustedUrl } from 'src/app/shared/functions/validate-url';
import { IStudentSearchRequest } from 'src/app/shared/interfaces/student-search';
import { IVarianceRequest } from 'src/app/shared/interfaces/variance-request';
import { IClassData, IClassDataRequest } from '../interfaces/class-data';
import { IPreschoolProfile } from '../interfaces/preschool-profile';
import { IRosterResponse, IRosterStudentRequest, IRosterSubmitData, IRosterSubmitRequest } from '../interfaces/roster-data';
import { IVarianceListResponse, IVarianceResponse } from '../interfaces/variance-data';

@Injectable({
  providedIn: 'root',
})
export class LocationProfileService {
  config: IAppConfig;

  constructor(
    private http: HttpClient,
    private appConfigService: AppConfigService,
  ) {
    this.config = this.appConfigService.getConfig();
  }

  public getLocationProfile(locationCode: string, buildingCode: string): Observable<IPreschoolProfile> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/PreschoolProfile/PreschoolProfile/${encodeURIComponent(locationCode)}/${encodeURIComponent(buildingCode)}`,
      this.config.apiUrl
    );
    return this.http
      .get<IPreschoolProfile>(url)
      .pipe(catchError(this.handleError));
  }

  public getClassesData(locationCode: string, buildingCode: string, schoolYear: string): Observable<IClassData[]> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/PreschoolProfile/GetPreschoolClasses/${encodeURIComponent(locationCode)}/${encodeURIComponent(buildingCode)}/${encodeURIComponent(schoolYear)}`,
      this.config.apiUrl
    );
    return this.http
      .get<IClassData[]>(url)
      .pipe(catchError(this.handleError));
  }

  public submitClassesData(classes: IClassDataRequest[]): Observable<ApiResponse<IClassDataRequest>> {
    return this.http
      .post<ApiResponse<IClassDataRequest>>(
        `${this.config.apiUrl}/PreschoolProfile/PreschoolClassesAdd`, classes,
      )
      .pipe(catchError(this.handleError));
  }

  public getVarianceListData(classSk: string): Observable<IVarianceListResponse[]> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/PreschoolProfile/GetVarianceList/${encodeURIComponent(classSk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<IVarianceListResponse[]>(url)
      .pipe(catchError(this.handleError));
  }

  public getVarianceData(varianceSk: string): Observable<IVarianceResponse> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/PreschoolProfile/GetVariance/${encodeURIComponent(varianceSk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<IVarianceResponse>(url)
      .pipe(catchError(this.handleError));
  }

  public submitVariance(varianceRequest: IVarianceRequest): Observable<ApiResponse<IVarianceRequest>> {
    return this.http
      .post<ApiResponse<IVarianceRequest>>(
        `${this.config.apiUrl}/PlacementFinder/VarianceRequest`, varianceRequest,
      )
      .pipe(catchError(this.handleError));
  }

  public getRosterData(classSk: string, monthYear: number | string): Observable<IRosterResponse> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/PreschoolProfile/RosterGet/${encodeURIComponent(classSk)}/${encodeURIComponent(String(monthYear))}`,
      this.config.apiUrl
    );
    return this.http
      .get<IRosterResponse>(url)
      .pipe(catchError(this.handleError));
  }

  public searchStudentsForRoster(searchRequest: IStudentSearchRequest): Observable<IRosterResponse> {
    return this.http
      .post<IRosterResponse>(
        `${this.config.apiUrl}/PreschoolProfile/SearchStudentForRoster`, searchRequest
      )
      .pipe(catchError(this.handleError));
  }

  public addStudent(studentData: IRosterStudentRequest): Observable<ApiResponse<IRosterStudentRequest>> {
    return this.http
      .post<ApiResponse<IRosterStudentRequest>>(
        `${this.config.apiUrl}/PreschoolProfile/RosterStudentAdd`, studentData,
      )
      .pipe(catchError(this.handleError));
  }

  public submitRoster(rosterData: {student: IRosterSubmitData, monthYearSk: number}): Observable<ApiResponse<{student: IRosterSubmitData, monthYearSk: number}>> {
    return this.http
      .post<ApiResponse<{student: IRosterSubmitData, monthYearSk: number}>>(
        `${this.config.apiUrl}/PreschoolProfile/RosterAdd`, rosterData,
      )
      .pipe(catchError(this.handleError));
  }

  public certifyRosters(rosterData: IRosterSubmitRequest): Observable<ApiResponse<IRosterSubmitRequest>> {
    return this.http
      .post<ApiResponse<IRosterSubmitRequest>>(
        `${this.config.apiUrl}/PreschoolProfile/RosterUpdate`, rosterData,
      )
      .pipe(catchError(this.handleError));
  }

  handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage: string = '';
    if (error.error instanceof ErrorEvent) {
      // Get client-side error
      errorMessage = error.error.message;
    } else {
      // Get server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    return throwError(() => {
      return errorMessage;
    });
  }
}
